/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { MerchantPortalFeatureFlag } from "constants/features"
import { getGrabXValueFromLocalStorage } from "grabx/getter"
import { isEmpty } from "lodash"
import {
  OutletStore,
  BusinessType,
  MerchantStoreInfo,
  OutletStoreV2,
  MerchantBusinessType,
} from "types/models/menu"
import { isKthBitSet } from "utils/misc"

export enum FeatureFlagBitPosition {
  martStore = 0,
  serviceBasedMenu = 1,
  brandListing = 2, // will be replace
  sellingTime = 3,
  departmentTagging = 4,
  mexItemAttributes = 6, // will be replace
  freeTextItemAttributes = 7, // will be replace
  numericalItemAttributes = 8, // will be replace
}

export enum FeatureFlagBitV2Position {
  serviceBasedMenu = 25,
  sellingTime = 26,
}

export const isMenuStoreVisible = (store: OutletStore | MerchantStoreInfo) => {
  const showInactiveStoreInMenu = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.portalShowInactiveStoreInMenu,
  )

  return (
    store.modelType === "Integrated" &&
    (store.status === "Yes" || showInactiveStoreInMenu)
  )
}

// default undefined buisinessType is GrabFood
export const isFoodStore = (store: OutletStore | MerchantStoreInfo) =>
  store?.businessType === undefined || store?.businessType === BusinessType.Food

export const isMartStore = (
  store: OutletStore | MerchantStoreInfo | MerchantBusinessType,
) => store?.businessType === BusinessType.Shop

export const isMartStoreV2 = (store: OutletStoreV2) =>
  !isEmpty(store?.merchantFlag?.grabMartFeatureEnabled)

export function isFeatureFlagEnabled(
  flag: FeatureFlagBitPosition,
  store?: OutletStore | null,
) {
  return isKthBitSet(store?.featureFlag ?? 0, flag)
}

export function isFeatureFlagV2Enabled(
  flag: FeatureFlagBitV2Position,
  store?: OutletStoreV2 | null,
) {
  return isKthBitSet(store?.featureFlag ?? 0, flag)
}

export const isItemAttributeStructure = (store?: OutletStore | null) =>
  isFeatureFlagEnabled(FeatureFlagBitPosition.mexItemAttributes, store)

export const isMartDepartmentStructure = (store: OutletStore) =>
  isFeatureFlagEnabled(FeatureFlagBitPosition.martStore, store)

export const isMartDepartmentV2Structure = (store: OutletStoreV2) =>
  !!store?.merchantFlag?.grabMartFeatureEnabled?.departmentEnabled

export const getIsSellingTimeStructure = (store: OutletStore) =>
  isFeatureFlagEnabled(FeatureFlagBitPosition.sellingTime, store)

export const getIsSellingTimeStructureV2 = (store: OutletStoreV2) =>
  isFeatureFlagV2Enabled(FeatureFlagBitV2Position.sellingTime, store)

export enum CombinedType {
  Menu = 0,
  Catalogue = 1,
  MenuCatalogue = 2,
}

export const businessTypeGrabXFacetVal: { [key: number]: string } = {
  [BusinessType.Food]: "FOOD",
  [BusinessType.Shop]: "MART",
}

export const getBusinessTypeGrabXFacetVal = (
  businessTypeNumber: BusinessType,
) => {
  return businessTypeGrabXFacetVal[businessTypeNumber]
}

export function getMerchantCombinedType(
  outlets: MerchantStoreInfo[] | MerchantBusinessType[] = [],
): CombinedType {
  const hasCatalogue = outlets.some(store => {
    return isMartStore(store)
  })

  const hasMenu = outlets.some(store => {
    return !isMartStore(store)
  })

  if (hasCatalogue && hasMenu) return CombinedType.MenuCatalogue

  if (hasCatalogue) return CombinedType.Catalogue

  return CombinedType.Menu
}
